import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE, PROJECT } from '../../constants/general.constants';
import {
  CreateObjectStorageRequest,
  ObjectStorageConnection,
  ObjectStorageProvider,
  ObjectStorageTestResult,
  TestObjectStorageRequest,
  UpdateObjectStorageRequest
} from '../../interfaces';

/**
 * Service for managing ObjectStorage connections.
 * Endpoints: /{projectId}/object-storage (CRUD) and /{projectId}/object-storage-test (POST).
 */
@Injectable({
  providedIn: 'root'
})
export class ObjectStorageService {
  readonly #httpClient = inject(HttpClient);

  private buildObjectStorageUrl(projectId: number, connectionId?: number): string {
    const baseUrl = `${API_BASE}/${PROJECT}/${projectId}/object-storage`;
    return connectionId ? `${baseUrl}/${connectionId}` : baseUrl;
  }

  list(projectId: number, provider?: ObjectStorageProvider): Observable<ObjectStorageConnection[]> {
    const url = this.buildObjectStorageUrl(projectId);
    const params = provider ? new HttpParams().set('provider', provider) : undefined;
    return this.#httpClient.get<ObjectStorageConnection[]>(url, { params });
  }

  get(projectId: number, connectionId: number): Observable<ObjectStorageConnection> {
    return this.#httpClient.get<ObjectStorageConnection>(this.buildObjectStorageUrl(projectId, connectionId));
  }

  create(projectId: number, request: CreateObjectStorageRequest): Observable<ObjectStorageConnection> {
    return this.#httpClient.post<ObjectStorageConnection>(this.buildObjectStorageUrl(projectId), request);
  }

  update(projectId: number, connectionId: number, request: UpdateObjectStorageRequest): Observable<ObjectStorageConnection> {
    return this.#httpClient.put<ObjectStorageConnection>(this.buildObjectStorageUrl(projectId, connectionId), request);
  }

  delete(projectId: number, connectionId: number): Observable<void> {
    return this.#httpClient.delete<void>(this.buildObjectStorageUrl(projectId, connectionId));
  }

  /**
   * Test an object storage connection configuration without saving it.
   * POST /api/project/{id}/object-storage-test
   *
   * Returns 200 with { success: true } on success,
   * or 400 with { success: false, message } on failure.
   */
  test(projectId: number, request: TestObjectStorageRequest): Observable<ObjectStorageTestResult> {
    const url = `${API_BASE}/${PROJECT}/${projectId}/object-storage-test`;
    return this.#httpClient.post<ObjectStorageTestResult>(url, request);
  }
}
